import { useEffect, useState, useCallback } from "react";
import { api, GBP, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, FileText, Upload, Trash2, Pencil, Star } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";
const CATEGORIES = ["Ebook", "Report", "Template", "Guide", "Worksheet", "Other"];
const empty = { title: "", description: "", price: "", category: "Ebook", cover_url: "", file_path: "", file_name: "", file_content_type: "", source_url: "", promo_video: "", featured: false, published: false };

function ProductDialog({ product, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(product ? { ...empty, ...product, price: String(product.price ?? "") } : empty);
  const [coverUploading, setCoverUploading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/media/image?folder=store/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_url", data.url); toast.success("Cover uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cover upload failed"); }
    finally { setCoverUploading(false); e.target.value = ""; }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setFileUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/store/admin/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, file_path: data.path, file_name: data.name, file_content_type: data.content_type }));
      toast.success("File uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "File upload failed"); }
    finally { setFileUploading(false); e.target.value = ""; }
  };

  const onVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setVideoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/store/admin/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("promo_video", data.video_url); toast.success("Promo video uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); }
    finally { setVideoUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    if (!(parseFloat(f.price) > 0)) { toast.error("Enter a price greater than 0"); return; }
    setSaving(true);
    try {
      const body = { ...f, price: parseFloat(f.price) };
      if (product) await api.put(`/store/admin/products/${product.id}`, body);
      else await api.post("/store/admin/products", body);
      toast.success(product ? "Product updated" : "Product created");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save product"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{product ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="sp-title" placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
          <textarea data-testid="sp-description" placeholder="Description" value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className={inputCls} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="sp-price" type="number" step="0.01" placeholder="Price £" value={f.price} onChange={(e) => set("price", e.target.value)} className={inputCls} />
            <select data-testid="sp-category" value={f.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>{CATEGORIES.map((o) => <option key={o}>{o}</option>)}</select>
          </div>

          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Cover image</label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-20 border border-[var(--line)] bg-[var(--alt)] flex items-center justify-center overflow-hidden shrink-0">
                {f.cover_url ? <img src={f.cover_url} alt="" className="w-full h-full object-cover" /> : <FileText size={20} className="text-[var(--ink-2)]" />}
              </div>
              <label data-testid="sp-cover-upload" className="cursor-pointer text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
                {coverUploading ? "Uploading…" : f.cover_url ? "Change cover" : "Upload cover"}
                <input type="file" accept="image/*" className="hidden" disabled={coverUploading} onChange={onCover} />
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Product file (PDF, ePub, ZIP, doc… max 50 MB)</label>
            <label data-testid="sp-file-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
              <Upload size={15} /> {fileUploading ? "Uploading…" : f.file_name ? "Replace file" : "Upload file"}
              <input type="file" className="hidden" disabled={fileUploading} onChange={onFile} />
            </label>
            {f.file_name && <p className="text-xs text-[var(--ink-2)] mt-1.5" data-testid="sp-file-name">Uploaded: {f.file_name}</p>}
            {!f.file_path && <p className="text-xs text-[var(--ink-2)] mt-1.5">No file yet — if you publish without a file it shows as <strong>"Coming soon"</strong> (buyers can join a waiting list). Upload the manuscript later to make it buyable.</p>}
          </div>

          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Promo video (optional — YouTube/Vimeo link or upload)</label>
            <input data-testid="sp-promo-video" placeholder="https://youtube.com/watch?v=…" value={f.promo_video} onChange={(e) => set("promo_video", e.target.value)} className={inputCls} />
            <div className="flex items-center gap-2 mt-2">
              <label data-testid="sp-video-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
                <Upload size={15} /> {videoUploading ? "Uploading…" : "Upload video (max 50 MB)"}
                <input type="file" accept="video/*" className="hidden" disabled={videoUploading} onChange={onVideo} />
              </label>
              {f.promo_video && <button type="button" onClick={() => set("promo_video", "")} className="text-xs text-[#B71C1C] hover:underline">Remove</button>}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
            <input type="checkbox" data-testid="sp-featured" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[var(--accent)]" /> Feature in the store hero (one shows at the top)
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer pt-1">
            <input type="checkbox" data-testid="sp-published" checked={f.published} onChange={(e) => set("published", e.target.checked)} className="accent-[var(--accent)]" /> Published (visible in the store)
          </label>
        </div>
        <DialogFooter><button onClick={save} disabled={saving} data-testid="sp-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] w-full disabled:opacity-60">{saving ? "Saving…" : (product ? "Save changes" : "Create product")}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StoreManager() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [waitlist, setWaitlist] = useState({});

  const load = useCallback(async () => {
    try {
      const [p, o, w] = await Promise.all([api.get("/store/admin/products"), api.get("/store/admin/orders"), api.get("/store/admin/waitlist")]);
      setProducts(p.data); setOrders(o.data); setWaitlist(w.data || {});
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this product? Buyers who already paid keep their download.")) return;
    try { await api.delete(`/store/admin/products/${id}`); toast.success("Product deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const revenue = orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount || 0), 0);
  const paidOrders = orders.filter((o) => o.payment_status === "paid");

  return (
    <div data-testid="store-manager">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="border border-[var(--line)] bg-white p-5"><div className="font-display font-extrabold text-2xl">{GBP(revenue)}</div><div className="text-sm text-[var(--ink-2)]">Store revenue</div></div>
        <div className="border border-[var(--line)] bg-white p-5"><div className="font-display font-extrabold text-2xl">{paidOrders.length}</div><div className="text-sm text-[var(--ink-2)]">Items sold</div></div>
        <div className="border border-[var(--line)] bg-white p-5"><div className="font-display font-extrabold text-2xl">{products.length}</div><div className="text-sm text-[var(--ink-2)]">Products</div></div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold">Products</h3>
        <ProductDialog onSaved={load} trigger={<button data-testid="store-new-product" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 hover:bg-[var(--accent-hover)] text-sm"><Plus size={16} /> New product</button>} />
      </div>
      <div className="border border-[var(--line)] bg-white overflow-x-auto mb-8">
        <table className="w-full text-sm" data-testid="store-products-table">
          <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]"><tr><th className="px-4 py-3">Product</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Waiting</th><th className="px-4 py-3">Sold</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[var(--line)]" data-testid={`store-product-row-${p.id}`}>
                <td className="px-4 py-3"><div className="font-semibold flex items-center gap-1.5">{p.featured && <Star size={13} className="fill-[var(--accent)] text-[var(--accent)]" />}{p.title}</div><div className="text-xs text-[var(--ink-2)]">{p.category}</div></td>
                <td className="px-4 py-3">{GBP(p.price)}</td>
                <td className="px-4 py-3">{!p.published ? <span className="text-[var(--ink-2)]">Draft</span> : p.file_path ? <span className="text-[#1B5E20] font-semibold">Published</span> : <span className="text-[var(--accent)] font-semibold">Coming soon</span>}</td>
                <td className="px-4 py-3" data-testid={`store-waitlist-${p.id}`}>{waitlist[p.id] || 0}</td>
                <td className="px-4 py-3">{p.sales_count || 0}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <ProductDialog product={p} onSaved={load} trigger={<button data-testid={`store-edit-${p.id}`} className="text-[var(--accent)] hover:underline inline-flex items-center gap-1 mr-3"><Pencil size={14} /> Edit</button>} />
                  <button onClick={() => remove(p.id)} data-testid={`store-delete-${p.id}`} className="text-[#B71C1C] hover:underline inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>
                </td>
              </tr>
            ))}
            {products.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-2)]">No products yet. Add your first one.</td></tr>}
          </tbody>
        </table>
      </div>

      <h3 className="font-display font-bold mb-3">Recent orders</h3>
      <div className="border border-[var(--line)] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="store-orders-table">
          <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]"><tr><th className="px-4 py-3">Buyer</th><th className="px-4 py-3">Product</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th></tr></thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[var(--line)]">
                <td className="px-4 py-3"><div className="font-semibold">{o.buyer_name}</div><div className="text-xs text-[var(--ink-2)]">{o.buyer_email}</div></td>
                <td className="px-4 py-3">{o.product_title}</td>
                <td className="px-4 py-3">{GBP(o.amount)}</td>
                <td className="px-4 py-3">{o.payment_status === "paid" ? <span className="text-[#1B5E20] font-semibold">Paid</span> : <span className="text-[var(--ink-2)] capitalize">{o.payment_status}</span>}</td>
                <td className="px-4 py-3 text-[var(--ink-2)]">{o.created_at ? new Date(o.created_at).toLocaleDateString("en-GB") : ""}</td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[var(--ink-2)]">No orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
